import { useSyncExternalStore } from "react";
import type { AuthProvider } from "ra-core";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  updateProfile,
  type User,
} from "firebase/auth";
import { auth } from "@/config/firebase";
import { canAccess } from "../commons/canAccess";
import { logger } from "@/utils/logger";
import { rememberPostLoginPath } from "@/utils/postLoginPath";
import { posthogCapture, posthogIdentify, posthogReset } from "@/config/posthog";
import {
  AUTH_TOKEN_COOKIE,
  AUTH_USER_COOKIE,
  setAuthCookie,
  setAuthJsonCookie,
  getAuthJsonCookie,
  removeAuthCookie,
} from "@/utils/authCookies";
const CURRENT_ENTITY_KEY = "raisy_current_entity_id";
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

interface StoredUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  administrator: boolean;
  entity_id?: string | null;
}

async function storeUser(firebaseUser: User): Promise<StoredUser> {
  const start = performance.now();
  logger.info("[AUTH][storeUser_010] start", { uid: firebaseUser.uid });

  let token: string | null = null;
  try {
    const tokenStart = performance.now();
    token = await firebaseUser.getIdToken();
    logger.info("[AUTH][storeUser_020] idToken fetched", { duration_ms: Math.round(performance.now() - tokenStart) });
    // D-006: token lives in a JS-readable cookie (SameSite=Lax, Path=/, 30d), not localStorage.
    setAuthCookie(AUTH_TOKEN_COOKIE, token);
  } catch (err) {
    logger.error("[AUTH][storeUser_025] failed to get ID token", err instanceof Error ? err : undefined);
  }

  // Admin status comes from the Firebase custom claim `admin` (granted via
  // POST /api/v1/user/set-admin). Picked up on login / next app load.
  let administrator = false;
  try {
    const tokenResult = await firebaseUser.getIdTokenResult();
    administrator = tokenResult.claims.admin === true;
  } catch (err) {
    logger.warn("[AUTH][storeUser_027] failed to read token claims", { error: (err as Error).message });
  }

  // Provision person entity for this user (idempotent)
  let entity_id: string | null = null;
  if (token) {
    try {
      const initStart = performance.now();
      logger.info("[AUTH][storeUser_030] calling /api/v1/user/init");
      const res = await fetch(`${API_URL}/api/v1/user/init`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const initDuration = Math.round(performance.now() - initStart);
      logger.info("[AUTH][storeUser_040] /user/init responded", { status: res.status, duration_ms: initDuration });
      if (res.ok) {
        const json = await res.json();
        entity_id = json?.data?.entity_id ?? null;
        logger.info("[AUTH][storeUser_050] entity_id resolved", { entity_id });
        // THE FUNNEL'S FIRST STEP (project091 T0108). `account_created` is the SERVER's answer to "was
        // this a signup or a login" — read from the flag rather than inferred from the response message,
        // so a copy edit cannot silence a conversion event. This is the only moment the backend can say
        // an account was created rather than found.
        if (json?.data?.account_created === true) {
          posthogCapture("signup_complete", { provider: "firebase" });
        }
      } else {
        const body = await res.text().catch(() => "<no body>");
        logger.warn("[AUTH][storeUser_045] /user/init non-ok", { status: res.status, body });
      }
    } catch (err) {
      logger.error("[AUTH][storeUser_060] /user/init threw", err instanceof Error ? err : undefined);
    }
  }

  const stored: StoredUser = {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName || firebaseUser.email,
    photoURL: firebaseUser.photoURL,
    administrator,
    entity_id,
  };
  setAuthJsonCookie(AUTH_USER_COOKIE, stored);
  logger.info("[AUTH][storeUser_100] complete", { uid: stored.uid, entity_id, duration_ms: Math.round(performance.now() - start) });
  return stored;
}

function getStoredUser(): StoredUser | null {
  return getAuthJsonCookie<StoredUser>(AUTH_USER_COOKIE);
}

function clearStoredUser() {
  removeAuthCookie(AUTH_USER_COOKIE);
  removeAuthCookie(AUTH_TOKEN_COOKIE);
}

/** The signed-in user's own person entity (provisioned via /user/init on login), or null. */
export function getUserEntityId(): string | null {
  return getStoredUser()?.entity_id ?? null;
}

/**
 * WHO IS PAYING — the two values a Stripe Payment Link can carry, and nothing else.
 *
 * `uid` is the FIREBASE uid, and that is the whole point of exposing it: it is the id our webhook
 * grants to (`Entity.identity_user_id`), so carrying it on the link makes the buyer unambiguous — it
 * cannot be mistyped at checkout and it does not change if he pays from a different email. `email` is
 * the weaker half, and the one glowtest_FE sends (`?prefilled_email=`) — it is also what our webhook
 * falls back to, so sending both means a typo cannot lose the credits.
 *
 * A guest gets `{uid: null, email: null}` — the popup then opens the bare link, exactly the fallback
 * glowtest uses.
 */
export function getPayerIdentity(): { uid: string | null; email: string | null } {
  const user = getStoredUser();
  return { uid: user?.uid ?? null, email: user?.email ?? null };
}

// ── Current-entity context ─────────────────────────────────────────
// The "current entity" the user is working on, app-wide. Defaults to the User
// entity; switchable from the top-bar EntitySwitcher. Persisted separately from
// the user record so the User entity identity is never overwritten.

const currentEntityListeners = new Set<() => void>();

export function getCurrentEntityId(): string | null {
  const override = localStorage.getItem(CURRENT_ENTITY_KEY);
  if (override) return override;
  return getUserEntityId();
}

export function setCurrentEntityId(entityId: string | null): void {
  if (entityId) {
    localStorage.setItem(CURRENT_ENTITY_KEY, entityId);
  } else {
    localStorage.removeItem(CURRENT_ENTITY_KEY);
  }
  currentEntityListeners.forEach((listener) => listener());
}

function subscribeCurrentEntity(listener: () => void): () => void {
  currentEntityListeners.add(listener);
  return () => {
    currentEntityListeners.delete(listener);
  };
}

/** Reactive read of the current entity id (re-renders on switch). */
export function useCurrentEntityId(): string | null {
  return useSyncExternalStore(subscribeCurrentEntity, getCurrentEntityId);
}

// ── Admin mode ─────────────────────────────────────────────────────
// Admins (Firebase custom claim `admin`) can switch between admin mode and
// user mode from the top bar. Admin-only nav items and CTAs render only when
// admin mode is on. Persisted in localStorage, defaults to off (user mode).

const ADMIN_MODE_KEY = "raisy_admin_mode";
const adminModeListeners = new Set<() => void>();

export function isAdministrator(): boolean {
  return getStoredUser()?.administrator ?? false;
}

export function getAdminMode(): boolean {
  return localStorage.getItem(ADMIN_MODE_KEY) === "on";
}

export function setAdminMode(on: boolean): void {
  localStorage.setItem(ADMIN_MODE_KEY, on ? "on" : "off");
  adminModeListeners.forEach((listener) => listener());
}

function subscribeAdminMode(listener: () => void): () => void {
  adminModeListeners.add(listener);
  return () => {
    adminModeListeners.delete(listener);
  };
}

/** Reactive: true only when the user is an admin AND admin mode is on. */
export function useAdminAccess(): boolean {
  const adminMode = useSyncExternalStore(subscribeAdminMode, getAdminMode);
  return isAdministrator() && adminMode;
}

// Wait for Firebase to resolve the current auth state (runs once on load).
export const firebaseReady = new Promise<User | null>((resolve) => {
  const start = performance.now();
  logger.info("[AUTH][firebaseReady_010] waiting for onAuthStateChanged");
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    unsubscribe();
    logger.info("[AUTH][firebaseReady_020] auth state resolved", { signed_in: !!user, duration_ms: Math.round(performance.now() - start) });
    if (user) {
      await storeUser(user);
      // project123: link a restored session to PostHog (full lane only — no-op
      // without consent, so the cookieless base lane stays anonymous).
      void posthogIdentify(user.uid);
    } else {
      clearStoredUser();
    }
    logger.info("[AUTH][firebaseReady_100] firebaseReady resolved", { signed_in: !!user, total_duration_ms: Math.round(performance.now() - start) });
    resolve(user);
  });
});

export interface RegisterParams {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

/**
 * Email registration (glowtest-aligned Register UX).
 * createUserWithEmailAndPassword → updateProfile(displayName) → force a token
 * refresh so the `name` claim reaches /user/init → storeUser() provisioning.
 * No credits redirect: raisy has no credit system (blessed deviation).
 */
export async function register(params: RegisterParams): Promise<void> {
  const start = performance.now();
  const displayName = `${params.firstName} ${params.lastName}`.trim();
  logger.info("[AUTH][register_010] start", { email: params.email });

  const credential = await createUserWithEmailAndPassword(
    auth,
    params.email,
    params.password,
  );
  logger.info("[AUTH][register_020] firebase user created", { uid: credential.user.uid, duration_ms: Math.round(performance.now() - start) });

  await updateProfile(credential.user, { displayName });
  logger.info("[AUTH][register_030] displayName set");

  // The cached ID token predates updateProfile — force a refresh so the
  // storeUser() call below sends a token whose `name` claim is the displayName.
  await credential.user.getIdToken(true);
  logger.info("[AUTH][register_040] id token refreshed");

  await storeUser(credential.user);
  void posthogIdentify(credential.user.uid);
  logger.info("[AUTH][register_100] complete", { uid: credential.user.uid, duration_ms: Math.round(performance.now() - start) });
}

/**
 * PAGES THAT ARE PUBLIC ON PURPOSE (D-115).
 *
 * The page a Stripe payment hands the buyer back to must render WITHOUT a session, because two real
 * buyers arrive with none: someone who paid through a Stripe Payment Link (they had no Raisy account a
 * minute ago), and anyone whose return from Stripe lands in a fresh context where the session is not
 * there. React Admin's `requireAuth` bounced both of them to `/login`, so the one page whose entire job
 * is to confirm a purchase showed a sign-in form instead — the PO's own report (2026-09-21): *"i dont
 * see anything here neither!"*.
 *
 * It is safe to serve because the page is built to be honest in exactly that state: with no session it
 * cannot read the wallet, so it says the account is the record instead of inventing a balance (see the
 * page's own note). Serving it never exposes anyone else's data — the read it makes is the caller's own
 * wallet, and it renders nothing when it cannot make it.
 */
const PUBLIC_PATHS = ["/payment-success"];

function isPublicPath(): boolean {
  const path = window.location.pathname;
  return PUBLIC_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

export const authProvider: AuthProvider = {
  /**
   * Login with email + password.
   * The RA LoginPage sends { email, password }.
   * For Google sign-in, pass { provider: "google" }.
   * For a WORKPLACE-SCOPED Google sign-in, also pass { hd } — the hosted domain Google should
   * restrict the chooser to. `hd` is a HINT to the chooser, never the enforcement: the
   * authoritative check is the ID token's own `hd` claim, verified server-side (@P294 T0101).
   */
  login: async (params: {
    email?: string;
    password?: string;
    provider?: "google";
    hd?: string;
  }) => {
    const start = performance.now();
    logger.info("[AUTH][login_010] start", { provider: params.provider ?? "email" });

    if (params.provider === "google") {
      const googleProvider = new GoogleAuthProvider();
      // Workspace scope, when the caller asked for one. Without this the popup offers every
      // Google account on the machine, which is not what "Sign in with Google Workplace" promises.
      if (params.hd) {
        googleProvider.setCustomParameters({ hd: params.hd });
        logger.info("[AUTH][login_012] google sign-in scoped to hosted domain", { hd: params.hd });
      }
      const popupStart = performance.now();
      const result = await signInWithPopup(auth, googleProvider);
      logger.info("[AUTH][login_020] google popup resolved", { duration_ms: Math.round(performance.now() - popupStart) });
      await storeUser(result.user);
      void posthogIdentify(result.user.uid);
      logger.info("[AUTH][login_100] complete", { provider: "google", duration_ms: Math.round(performance.now() - start) });
      return;
    }

    if (!params.email || !params.password) {
      throw new Error("Email and password are required");
    }

    const credentialStart = performance.now();
    const credential = await signInWithEmailAndPassword(
      auth,
      params.email,
      params.password,
    );
    logger.info("[AUTH][login_020] firebase signIn resolved", { duration_ms: Math.round(performance.now() - credentialStart) });
    await storeUser(credential.user);
    void posthogIdentify(credential.user.uid);
    logger.info("[AUTH][login_100] complete", { provider: "email", duration_ms: Math.round(performance.now() - start) });
  },

  logout: async () => {
    await signOut(auth);
    clearStoredUser();
    // The current-entity context is IDENTITY-scoped state and is read back unvalidated
    // (getCurrentEntityId returns the stored value as-is). Leaving it behind hands the NEXT
    // user on this browser the previous user's company: every company-scoped surface then
    // opens on a company they never chose — a public one still answers reads, so they would
    // see another company's prepared work. Clear it with the rest of the stored identity.
    setCurrentEntityId(null);
    // project123: stop linking events to the previous user on the next session.
    posthogReset();
  },

  checkAuth: async () => {
    if (isPublicPath()) {
      logger.info("[AUTH][checkAuth_005] public page — no session required", { path: window.location.pathname });
      return;
    }
    const start = performance.now();
    logger.info("[AUTH][checkAuth_010] start");
    await firebaseReady;
    const user = auth.currentUser;
    logger.info("[AUTH][checkAuth_020] firebaseReady awaited", { signed_in: !!user, duration_ms: Math.round(performance.now() - start) });
    if (!user) {
      clearStoredUser();
      // A signed-out visitor who opened a deep link must come back to it after
      // signing in. The public doorway's payoff sends him to /email, and React
      // Admin hands the login page no destination — without this he would land
      // on the default surface and the prepared emails would look lost.
      rememberPostLoginPath();
      throw new Error("Not authenticated");
    }
    // Refresh the local token only — storeUser (which calls /user/init) runs on
    // login and the firebaseReady listener; calling it here would hit /user/init
    // on every React Admin route check.
    try {
      const token = await user.getIdToken();
      setAuthCookie(AUTH_TOKEN_COOKIE, token);
      logger.info("[AUTH][checkAuth_030] token refreshed");
    } catch (err) {
      logger.warn("[AUTH][checkAuth_035] token refresh failed", { error: (err as Error).message });
    }
  },

  checkError: async (error: { status?: number }) => {
    if (error.status === 401 || error.status === 403) {
      clearStoredUser();
      // An expired session returns him to the surface he was working on.
      rememberPostLoginPath();
      throw new Error("Session expired");
    }
  },

  getIdentity: async () => {
    const user = getStoredUser();
    if (!user) throw new Error("Not authenticated");
    return {
      id: user.uid,
      fullName: user.displayName || user.email || "User",
      avatar: user.photoURL ?? undefined,
    };
  },

  canAccess: async ({ signal: _signal, ...params }) => {
    const user = getStoredUser();
    if (!user) return false;
    const role = user.administrator ? "admin" : "user";
    return canAccess(role, params);
  },

  getPermissions: async () => {
    const user = getStoredUser();
    if (!user) return null;
    return { role: user.administrator ? "admin" : "user", uid: user.uid };
  },
};
